import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { hasPermission } from 'hooks'
import { Stack, Title, Button, Paper, Text, NavigationButton } from 'components'
import { IconPlayerPlayFilled } from '@tabler/icons-react'
import { TeacherFinalAssessmentDrawer } from 'pages/TaskTracker/assessmentDrawer/finalAssessmentDrawer'
import { StudentFinalAssessmentDrawer } from 'pages/StudentTasks/finalAssessmentDrawer'

interface ProjectFinalAssessmentProps {
  projectId: string
  projectName: string
  projectStatus: string
  // Null while the project has no kind loaded (equality checks handle it).
  projectKind: string | null
  finalAssessmentStarted: boolean
  startFinalAssessmentModal: (id: string) => void
  finalReportURL: (id: string) => string
  onFinalReportClick: () => void
}

const isTutorOrManager = () => hasPermission(['tutor', 'manager'])
const canViewFinalReport = () => hasPermission(['guardian', 'manager', 'student', 'tutor'])
const canViewFinalAssessment = () => hasPermission(['manager', 'student', 'tutor'])

export const ProjectFinalAssessment = ({
  projectId,
  projectName,
  projectStatus,
  projectKind,
  finalAssessmentStarted,
  startFinalAssessmentModal,
  finalReportURL,
  onFinalReportClick,
}: ProjectFinalAssessmentProps) => {
  const { t } = useTranslation()
  // The final assessment opens in a right-side drawer for both profiles:
  // tutors/managers get the teacher drawer (AR-579) and students get their
  // own self-assessment drawer (AR-779), instead of navigating to the legacy
  // page. A user is only ever one profile, so a single open flag is enough.
  const [drawerOpened, setDrawerOpened] = useState(false)

  const isApplication = projectKind === 'application'
  const isFinished = projectStatus === 'finished'
  const isApproved = projectStatus === 'approved'
  const isDraft = projectStatus === 'draft'

  if (!isApplication || isDraft) return null

  if (isFinished && canViewFinalReport()) {
    return (
      <Paper withBorder p='lg' radius='md'>
        <Stack spacing='md' align='flex-start'>
          <Title order={2}>{t('new_project_view.title_final_report')}</Title>
          <NavigationButton
            variant='filled'
            size='lg'
            url={finalReportURL(projectId)}
            onClick={onFinalReportClick}
          >
            {t('new_project_view.button_final_report')}
          </NavigationButton>
        </Stack>
      </Paper>
    )
  }

  if (!isApproved || !canViewFinalAssessment()) return null

  const isTeacher = isTutorOrManager()
  const descriptionKey = finalAssessmentStarted
    ? isTeacher
      ? 'new_project_view.final_assessment_description_teacher_started'
      : 'new_project_view.final_assessment_description_student_started'
    : isTeacher
      ? 'new_project_view.final_assessment_description_teacher_not_started'
      : 'new_project_view.final_assessment_description_student_not_started'

  return (
    <Paper withBorder p='lg' radius='md'>
      <Stack spacing='md' align='flex-start'>
        <Title order={2}>{t('new_project_view.title_final_assessment')}</Title>
        <Text size='md' color='dimmed'>
          {t(descriptionKey)}
        </Text>
        {!finalAssessmentStarted && isTeacher && (
          <Button
            variant='filled'
            size='lg'
            leftIcon={<IconPlayerPlayFilled />}
            onClick={() => startFinalAssessmentModal(projectId)}
          >
            {t('new_project_view.button_start_final_assessment')}
          </Button>
        )}
        {finalAssessmentStarted && isTeacher && (
          <Button variant='filled' size='lg' onClick={() => setDrawerOpened(true)}>
            {t('new_project_view.button_submit_final_assessment')}
          </Button>
        )}
        {finalAssessmentStarted && !isTeacher && (
          <Button variant='filled' size='lg' onClick={() => setDrawerOpened(true)}>
            {t('new_project_view.button_student_submit_final_assessment')}
          </Button>
        )}
      </Stack>

      {isTeacher && (
        <TeacherFinalAssessmentDrawer
          opened={drawerOpened}
          projectId={projectId}
          projectName={projectName}
          onClose={() => setDrawerOpened(false)}
        />
      )}

      {!isTeacher && (
        <StudentFinalAssessmentDrawer
          opened={drawerOpened}
          identifierId={projectId}
          onClose={() => setDrawerOpened(false)}
        />
      )}
    </Paper>
  )
}
